/* Серверный режим «Повтори движения» и «Стать персонажем» (dance, пресеты dance и swap) на движке шаблона (план вехи 3 §0 D1, план движений §2.1).
   createDance(createDanceServer) (features/dance/engine.ts) отдаёт сюда крючки экрана, а каждая вставка SV спрашивает этот объект (SRV):
   цена, готовность, кто повторит, голос, ролик и фото, создание и хвост — только из сервиса заказа кадра (OrderService привязки EngineFrame).
   Правила:
   - одна привязка — один кадр ServerStudio (ключ кадра и его сервис заказа): у другого кадра — и у нового сервиса под прежним ключом —
     своё S, свой мост загрузок, свой хвост BotRun (фасад TAIL пересоздаёт BotRun при смене кадра); пресет — по политике формы и входу приложения
     (своя заявка и свой ключ у каждого приложения: «Повтори движения» 7018, «Стать персонажем» 7100); переключателя пресетов в шапке нет (Q-M6);
   - движок не пишет в sessionStorage и не зовёт создание сам: заявку, ключ, отправку, опрос и отказы ведёт сервис (run-driver.ts); при открытии
     ничего не отправляется, только показ;
   - S → входы: одна точка — syncFoot движка (commit, inputs-mirror: под замком не пишет, равные входы не пишет); входы → S — только когда
     входы сменились не зеркалом (повтор из «Истории», «Изменить» после перезагрузки, заявка родной формы): dance-inputs.viewOf → applyView;
     id загрузок пишет мост загрузок, зеркало только снимает фото, если повторит не человек с фото;
   - файл сначала идёт серверу (admit): ролик — политика поля, лист согласия (права и consent_accepted вместе), одна очередь загрузок
     (media-bridge), только принятый ролик читает шаблон; фото — тип, вес и согласие до кадра, загрузка — после «Готово» кадра; превью повтора —
     фото с сервера, ролик — загрузить заново (Q7);
   - пределы фрагмента — duration_limits политики по фону (Q-M1); «Человек в кадре» и «нет человека» — только по проверке сервера (dancer),
     пока она не опубликована — ни того, ни другого (Q-M2);
   - голос: «Голос героя» — голос карточки персонажа по умолчанию; «Другой голос» подтверждает сервер (voice-pick, только с переозвучкой);
     строки голоса и фона — только опубликованные значения формы; надбавка — только из политики;
   - суммы на странице заказа — только из заказа (bot-run-server: receipt, failText); цена на кнопке — оценка по цене за секунду приложения
     (как родная форма), в заказ уходит цена сервиса; скидка — только если форма её публикует. */
import { endpoints } from '../../api';
import type { AvatarDetails } from '../../api/avatar-catalog';
import type { Voice } from '../../api/types';
import { avatarThumb, portraitOrb, voiceOrb } from '../../bridge/voice-orb';
import { accountLocale, uiFormat, uiText } from '../../i18n';
import { circleBalance, forgetCircleBalance, rememberCircleBalance } from '../studio/circle/tool-helpers';
import { cNum, mmss, tailCurrent, tailSteps } from '../studio/circle/order-tail-model';
import { toolField } from '../studio/circle/tool-form';
import { leaseSkins, skinUrl } from '../studio/engine-kit/char-cards';
import { safeMediaUrl } from '../studio/studio-content';
import { studioCheapOffer, studioUnitPrice, type StudioApp, type StudioForm } from '../studio/studio-api';
import { failText, receipt, serverFoot, esc, type BotRunApi, type BotRunRun } from '../studio/engine-kit/bot-run-server';
import { consentField, openConsentSheet, type ConsentSheet } from '../studio/engine-kit/consent-sheet';
import { ENGINES, type EngineBinding, type EngineBound, type EngineKind, type EngineScreen } from '../studio/engine-kit/engine-host';
import { createInputsMirror, sameInputs, type InputsMirror } from '../studio/engine-kit/inputs-mirror';
import { createStudioDraft, type StudioDraft } from '../studio/engine-kit/studio-draft';
import { motionDraftKey } from '../motion/use-motion-draft';
import { createMediaBridge, mediaFile, mediaPolicy, type MediaBridge, type MediaItem, type MediaPolicy } from '../studio/engine-kit/media-bridge';
import { uploadFailureText, type Intent, type OrderService, type OrderSnapshot } from '../studio/engine-kit/order-service';
import { peopleModule, personRef, selfPicture, selfRef, SILHOUETTE, watchPeople, type PeopleModule } from '../studio/engine-kit/people-bridge';
import { RESULT_FRAMES, createResultMedia, type ResultMedia } from '../studio/engine-kit/result-media';
import { createRunDriver, peekIntent, type RunDriver } from '../studio/engine-kit/run-driver';
import { screenDoc, type ScreenDoc } from '../studio/engine-kit/screen-doc';
import { poorBalance, serverUi, type ServerUi } from '../studio/engine-kit/server-ui';
import { chatClick, chatFoot, doneP, openSettings } from '../studio/engine-kit/tail-done';
import { createVoicePick, type VoicePick } from '../studio/engine-kit/voice-pick';
import { motionCircleAppId, motionToCircle } from '../motion/motion-result-target';
import {
  motionPolicy,
  motionPrice,
  motionUnit,
  motionVoiceKey,
  motionVoiceModes,
  motionVoiceUnit,
  motionWords,
  type MotionPreset,
} from '../motion/motion-model';
import {
  applyView,
  danceBackgrounds,
  danceLimits,
  danceMissing,
  dancePreset,
  inputsOf,
  skinOf,
  viewFromEngine,
  viewOf,
  type DanceCrop,
  type DanceView,
  type DanceViewHooks,
} from './dance-inputs';

/* ---------- контракт с движком (features/dance/engine.ts, serve({...})) ---------- */

type Kind = 'video' | 'photo';
type Inputs = Record<string, string>;
/** Ролик в S шаблона (BotCore.files.clip): {name, size, url, video, poster, w, h, dur, mute, trim, cut, nobody} */
export interface EngineClip {
  name: string;
  size: number;
  url: string;
  video?: string | null;
  poster?: string;
  w?: number;
  h?: number;
  dur?: number;
  mute?: boolean;
  nobody?: boolean;
  trim?: { from: number; to: number };
  [key: string]: unknown;
}
/** Снимок человека в S шаблона (BotCore.files.image): {name, size, url, w, h, crop} */
export interface EnginePhoto {
  name: string;
  size: number;
  url: string;
  w?: number;
  h?: number;
  crop?: DanceCrop | null;
  [key: string]: unknown;
}
/** Настройки страницы (BotCore.uiDefaults и init каркаса): то, что читает SRV */
export interface EngineUi {
  buzz(kind?: string): void;
  tip(el: Element | null, text: string): void;
  voices(): unknown[];
  myVoices(): unknown[];
  chars?: unknown[];
  [key: string]: unknown;
}
interface EngineUpload {
  o: { mb?: number; [key: string]: unknown };
  say(text: string): void;
  paint(): boolean;
}
interface EngineFoot {
  poor(): boolean;
  poorOpen(): void;
  sync(): void;
}
interface EngineCard {
  sync(): void;
  close(): void;
  readonly isOpen?: boolean;
}
interface EnginePeople {
  setIn(on: boolean): void;
  inFrame(): boolean;
  paintForm(): void;
  close(): void;
}
/** Слот карточки «Ещё настройки» (BotCore.settingsCard) */
interface SettingSlot {
  k: string;
  t: string;
  kind: string;
  cur?: string;
  opts?: unknown[][];
  hint?: string;
  [key: string]: unknown;
}
/** Персонаж страницы (ShellChar сервера) */
interface EngineChar {
  id: number | string;
  name?: string;
  img?: string;
  voices?: unknown[];
  sex?: string;
}
/** Крючки, которые движок отдаёт serve (engine.ts, блок SV перед разметкой формы): части экрана ниже по файлу — геттеры */
export interface DanceEngineHooks extends DanceViewHooks {
  S: DanceViewHooks['S'] & {
    clip: EngineClip | null;
    ph: EnginePhoto | null;
    crop: unknown;
    cheap: boolean;
    chFirst: unknown[];
  };
  PRE: Record<MotionPreset, Record<string, unknown>>;
  readonly P: EnginePeople;
  readonly UP: EngineUpload;
  readonly FOOT: EngineFoot;
  readonly TAIL: BotRunApi;
  readonly SC: EngineCard;
  readonly VC: { close(): void; readonly on: boolean };
  root(): HTMLElement | null;
  mounted(): boolean;
  ui(): EngineUi;
  ic(name: string): string;
  esc(value: string): string;
  whoReady(): boolean;
  meVar(): { id?: unknown; name?: string; voices?: unknown[] } | null;
  dancer(): { id: number; n: string } | null;
  curChar(): EngineChar | null;
  charOf(id: unknown): EngineChar | null;
  clipLen(): number;
  clipErr(): string;
  arNow(): string;
  vconv(): boolean;
  mute(): boolean;
  whoName(): string;
  fragOn(): boolean;
  setLimits(min: number, max: number): void;
  setup(o: unknown): boolean;
  presetSet(k: string): boolean;
  prep(): void;
  paintPage(): void;
  clipPaint(): void;
  whoSync(): void;
  syncFoot(): void;
  issSync(): void;
  moreSync(): void;
  readClip(file: File): void;
  readPh(file: File): void;
  cropEnd(ok: boolean): void;
  stop(): void;
  runSteps(): string[][];
}
/** Голос в виде шаблона (строки слота «Голос», каталог страницы): шар — строка CSS; is_s2s — строго с сервера */
export interface EngineVoice {
  id: number;
  name: string;
  orb: string;
  tags: string[];
  sex: string | null;
  c: string[];
  mine: boolean;
  is_s2s: boolean;
  preview_url: string | null;
}

/** Фигура в танце цветом силуэта «Вы» (SILHOUETTE) — «До» примера «Повтори движения»: кадр ролика с движениями без чужого персонажа */
const DANCER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 90"><rect width="60" height="90" fill="#2c2c2e"/><g fill="none" stroke="#8e8e93" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"><path d="M33 30 29 52M32 33l11-9 7-11M31 35l-12 5-8-6M29 52l9 14-2 14M29 52l-8 15-10 7"/></g><circle cx="35" cy="21" r="6" fill="#8e8e93"/></svg>',
  );

export interface DanceServer {
  ui<U extends object>(base: U): ServerUi<U>;
  blank(): string;
  /** «До» примера в пустой рамке: нейтральная фигура в движении вместо демо-кадра прототипа (lucia.png — персонаж чужой игры, владелец 02.11.2026) */
  before(): string;
  meUrl(): string;
  entry(o: unknown): Record<string, unknown>;
  open(o: unknown): void;
  close(): void;
  tail(create: (o: Record<string, unknown>) => BotRunApi, opts: Record<string, unknown>): BotRunApi;
  wrap<T>(slot: 'cheap', value: T): T;
  camText(): string;
  nobody(): boolean;
  voice(id: unknown): EngineVoice | null;
  heroVoice(): EngineVoice | null;
  price(cheap: boolean): number;
  missing(): string | null;
  ready(): string;
  missText(k: string): string | null;
  issue(): string | null;
  items(L: SettingSlot[]): SettingSlot[];
  pickVoice(id: unknown): void;
  label(): string | null;
  busy(): boolean;
  badge(): string;
  sub(): string;
  note(): string;
  upOpts(): Record<string, unknown>;
  trimT(): { max: string; upto: string };
  commit(): void;
  admit(file: File, kind: Kind): boolean;
  accept(c: EngineClip, kind: 'video'): void;
  remove(kind: Kind): void;
  photoRead(f: EnginePhoto, file: File): boolean;
  photoChosen(f: EnginePhoto): void;
  conv(): boolean;
  clipStub(): EngineClip;
  doneText(sn: Record<string, unknown>): string;
  click(d: DOMStringMap, el: HTMLElement, r: BotRunRun | null): boolean;
  start(btn: HTMLElement): void;
  steps(): string[][];
  snap(): Record<string, unknown>;
  sentPrice(): number;
  drive(r: BotRunRun): void;
  failText(r: BotRunRun): string | null;
  receipt(r: BotRunRun): string;
}

/* ---------- общие части ---------- */

interface Core {
  html: { unit(n: number, rest: string): string; note(kind: string, text: string): string };
  saveBtn(btn: HTMLElement, make: () => Promise<{ data: Blob; name: string }>, o: { buzz(): void; tip(el: Element, text: string): void }): Promise<unknown>;
}
const core = () => window.BotCore as unknown as Core;
function busyButton<T>(btn: HTMLElement | null, fn: () => Promise<T>): Promise<T | undefined> {
  const busy = window.SteosUI?.busy;
  return btn && busy ? busy(btn, fn) : fn();
}
const MB = 1024 * 1024;
/** Предел ролика, если поле его не публикует (как MotionClip: 100 МБ) */
const VIDEO_MB = 100;
/** accept рамки BotCore.upload (её проверка типа — до моста): любой ролик и расширения без типа у браузера; точные типы и вес — у моста */
const ACCEPT = 'video/*,.mp4,.m4v,.mov,.qt,.webm';
const TYPE_NAMES: Record<string, string> = { 'video/mp4': 'MP4', 'video/quicktime': 'MOV', 'video/webm': 'WebM', 'image/jpeg': 'JPG', 'image/png': 'PNG', 'image/webp': 'WebP' };
const TRANSPORT = uploadFailureText(null);
const FORM_SAY = [
  'Некоторые файлы повтора больше недоступны. Загрузите их заново.',
  'Этот инструмент сейчас недоступен. Попробуйте обновить форму.',
  'Инструмент обновляется. Обновите форму позже.',
];
/** Коды missing() шаблона: их слова — у шаблона (missText); всё остальное — уже готовая строка сервера */
const CODES = new Set(['crop', 'rec', 'clip', 'short', 'body', 'frag', 'who', 'person', 'ph', 'me', 'cast']);
/** Правило согласия ряда (MotionConsent): права на материалы и согласие человека на фото — одним касанием */
const RULE = 'Только свои фото или с согласием человека на фото';
const num = (value: unknown): number | null => {
  const n = typeof value === 'string' && /^[1-9][0-9]*$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isSafeInteger(n) && n > 0 ? n : null;
};
const locale = () => (accountLocale() === 'ru' ? 'ru' : 'en');
function engineVoice(voice: Voice): EngineVoice {
  return {
    id: voice.id,
    name: voice.name,
    orb: voiceOrb(voice) ?? '',
    tags: Array.isArray(voice.tags) ? voice.tags : [],
    sex: voice.sex ?? null,
    c: [],
    mine: !!voice.is_mine,
    is_s2s: voice.is_s2s === true,
    preview_url: voice.preview_url ?? null,
  };
}
/** Голос каталога оболочки (ui.voices / ui.myVoices) в виде шаблона; is_s2s — только явное true */
function shellVoice(raw: unknown): EngineVoice | null {
  const v = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const id = num(v?.id);
  if (!v || id === null) return null;
  return {
    id,
    name: typeof v.name === 'string' ? v.name : '',
    orb: typeof v.orb === 'string' ? v.orb : '',
    tags: Array.isArray(v.tags) ? v.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    sex: typeof v.sex === 'string' ? v.sex : null,
    c: Array.isArray(v.c) ? v.c.filter((x): x is string => typeof x === 'string') : [],
    mine: v.mine === true || v.is_mine === true,
    is_s2s: v.is_s2s === true,
    preview_url: typeof v.preview_url === 'string' ? v.preview_url : null,
  };
}
/** «MP4 или MOV»: типы политики словами (строка ограничений рамки шаблона — без веса) */
function typeWords(accept: string[]): string {
  const list = [...new Set(accept.map(mime => TYPE_NAMES[mime] ?? mime.split('/')[1]?.toUpperCase()).filter(Boolean))];
  return list.length > 1 ? uiFormat('{0} или {1}', list.slice(0, -1).join(', '), list.at(-1)) : (list[0] ?? '');
}
/** Запись в рамке даёт тип, который форма принимает (Q-M7): иначе «Снять» не показывается */
function canRecordAccepted(): boolean {
  const recorder = (globalThis as unknown as { MediaRecorder?: { isTypeSupported?(type: string): boolean } }).MediaRecorder;
  return !!recorder?.isTypeSupported && ['video/mp4', 'video/quicktime'].some(type => recorder.isTypeSupported!(type));
}

/* ---------- карточки персонажей (общие для кадров; язык — в ключе) ---------- */

const details = new Map<string, AvatarDetails | null>();
const detailsFlight = new Map<string, Promise<AvatarDetails | null>>();
const detailsKey = (id: number) => `${id}:${locale()}`;
function loadDetails(id: number): Promise<AvatarDetails | null> {
  const key = detailsKey(id);
  if (details.has(key)) return Promise.resolve(details.get(key) ?? null);
  let flight = detailsFlight.get(key);
  if (!flight) {
    flight = endpoints.avatarCatalog
      .details(id, locale())
      .then(
        value => (value?.avatar?.id === id ? value : null),
        () => null,
      )
      .then(value => {
        details.set(key, value);
        return value;
      })
      .finally(() => detailsFlight.delete(key));
    detailsFlight.set(key, flight);
  }
  return flight;
}

/** Подпись итога (bot-dance.js doneText, родной motionDoneText) целыми фразами словаря: у танца — кто повторил, у замены — герой в вашем видео;
    звук — только если сервер его сохраняет (policy.original_audio_preserved и has_audio итога). Доставку решает tail-done (doneP) */
function doneText(sn: Record<string, unknown>, audio: boolean): string {
  const voice = sn.vm !== 'src' && sn.vn ? String(sn.vn) : '';
  const me = sn.who === 'me';
  const who = me ? uiText('Вы') : sn.who === 'ph' ? uiText('Человек с фото') : String(sn.name ?? '') || uiText('Герой');
  const tail = uiText('Бот уже прислал видео в чат, в отдельную тему.');
  if (sn.pre === 'swap') {
    const sound = voice ? uiFormat('голос «{0}»', voice) : audio ? uiText('звук как в видео') : '';
    return `${sound ? uiFormat('{0} теперь в вашем видео: движения и мимика ваши, {1}.', who, sound) : uiFormat('{0} теперь в вашем видео: движения и мимика ваши.', who)} ${tail}`;
  }
  const rest = voice ? uiFormat('голос «{0}», формат тот же', voice) : uiText(audio ? 'звук и формат те же' : 'формат тот же');
  const line = me
    ? uiFormat('Вы повторили движения из видео, {0}.', rest)
    : uiFormat(sn.g === 'f' ? '{0} повторила движения из видео, {1}.' : '{0} повторил движения из видео, {1}.', who, rest);
  return `${line} ${tail}`;
}

/** Состояние одной привязки (кадра ServerStudio) */
interface Frame {
  b: EngineBinding;
  key: string;
  svc: OrderService;
  app: StudioApp;
  bridge: MediaBridge;
  driver: RunDriver;
  mirror: InputsMirror;
  /** Форма, для которой прошла первая сверка (пределы, пресет, входы → S) */
  form: StudioForm | null;
  /** Входы без id загрузок, которые S уже знает (своя запись зеркала или перенос в S) */
  known: Inputs | null;
  /** Вход экрана (контракт setup шаблона: пресет, кто повторит, фон, голос) */
  entry: Record<string, unknown>;
  intentSeen: string;
  formWait: boolean;
  formAt: StudioForm | null;
  prefill: Inputs | null;
  /** Файлы, которые сервер принял: только их читает шаблон (admit) */
  approved: WeakSet<File>;
  /** Файл снимка шаблона (BotCore.files.image) → File: загрузка после «Готово» кадра */
  photoFiles: WeakMap<object, File>;
  /** Снимки, которые пришли превью повтора (без кадра) */
  restoredPhotos: WeakSet<File>;
  /** Запись моста, файл которой сейчас в S */
  items: Partial<Record<Kind, MediaItem>>;
  urls: Set<string>;
  restoring: Partial<Record<Kind, string>>;
  /** Кадр и фрагмент из входов для файла, который ещё восстанавливается */
  pending: Partial<Pick<DanceView, 'crop' | 'trim'>>;
  said: string;
  consent: ConsentSheet | null;
  /** Черновик входов: ключ и хранилище родной формы движений (откат движок ↔ родная форма ничего не теряет) */
  draft: StudioDraft;
  pick: VoicePick;
  voices: Map<number, EngineVoice>;
  peek: Intent | null;
  formShown: boolean;
  balanceAsk: boolean;
  orderSeen: number | undefined;
  media: ResultMedia;
  /** Документ экрана (result_actions: «Сделать кружок») */
  doc: ScreenDoc<Record<string, unknown>>;
  /** Идёт передача итога в «Кружок из видео» */
  moving: AbortController | null;
}

/* ---------- сервер экрана ---------- */

let instance: { bind(b: EngineBinding): EngineBound } | null = null;

export function createDanceServer(h: DanceEngineHooks): DanceServer {
  const { S } = h;
  /** Начальное S шаблона: новый кадр начинает с него */
  const S0 = JSON.stringify(S);
  let frame: Frame | null = null;
  let bound: Frame | null = null;
  let painted: Frame | null = null;
  let tailOpts: { create: (o: Record<string, unknown>) => BotRunApi; opts: Record<string, unknown> } | null = null;
  let inst: BotRunApi | null = null;
  let startSwap = false;
  const facade = {} as BotRunApi;

  const cur = () => (bound && bound === frame ? frame : null);
  const live = (f: Frame) => {
    const s = f.svc.get();
    return { s, d: f.svc.derive(s) };
  };
  const onForm = () => h.mounted() && !h.TAIL.run;
  const formOf = (f: Frame | null = cur() ?? frame) => f?.svc.get().form ?? null;
  /** Входы, которые определяют вид S: id загрузок пишет мост, согласие — лист согласия (viewOf их не читает) — их смена S не трогает,
      иначе «Принять» листа прав при загрузке ролика перерисовывало бы страницу (у шаблона clipAccept → clipPaint на месте) */
  const strip = (inputs: Inputs): Inputs => {
    const out = { ...inputs };
    delete out.video;
    delete out.photo;
    delete out.consent_accepted;
    return out;
  };
  /** Подсказка-стекло у элемента; элемента нет на месте — у кнопки подвала */
  const tip = (el: Element | null, text: string) => {
    const at = el?.isConnected ? el : (h.root()?.querySelector('#dnFoot .btn') ?? null);
    if (text && at) h.ui().tip(at, uiText(text));
  };
  const intentOf = (f: Frame | null): Intent | null => {
    const s = f?.svc.get();
    if (!f || !s) return null;
    return s.intent ?? (!s.identity && !s.integrityError ? f.peek : null);
  };
  /** Политика поля: ролик без своего веса — 100 МБ (как родная рамка) */
  function policyOf(f: Frame, key: string): MediaPolicy | null {
    const form = f.svc.get().form;
    if (key !== 'video') return mediaPolicy(form, key);
    const field = toolField(form, 'video');
    return mediaPolicy(form, 'video', field?.upload?.max_bytes ? {} : { maxBytes: VIDEO_MB * MB });
  }
  /** Голос персонажа по умолчанию: карточка персонажа (default_voice_id, иначе первый голос), пока её нет — первый голос ряда */
  function charVoiceId(): number | null {
    if (h.who() !== 'char') return null;
    const c = h.curChar();
    const id = num(c?.id);
    if (!c || !id) return null;
    const card = details.get(detailsKey(id));
    if (card) return card.avatar.default_voice_id ?? card.avatar.voice_ids[0] ?? null;
    return num(Array.isArray(c.voices) ? c.voices[0] : null);
  }
  const uploadsOf = (f: Frame): DanceView['uploads'] => {
    const out: DanceView['uploads'] = {};
    const video = f.items.video;
    if (video?.state === 'ready' && video.uploadId && S.clip) out.video = video.uploadId;
    const photo = f.items.photo;
    if (photo?.state === 'ready' && photo.uploadId && S.ph) out.photo = photo.uploadId;
    return out;
  };

  /* ---- голоса ---- */
  function voice(id: unknown): EngineVoice | null {
    const want = num(id);
    if (want === null) return null;
    const f = cur() ?? frame;
    const picked = f?.voices.get(want);
    if (picked) return picked;
    const own = f?.svc.get().voice;
    if (own && own.id === want) return engineVoice(own);
    const ui = h.ui();
    for (const list of [ui.voices?.() ?? [], ui.myVoices?.() ?? []])
      for (const raw of list) {
        const v = shellVoice(raw);
        if (v && v.id === want) return v;
      }
    for (const card of details.values()) {
      const v = card?.voices.find(item => item.id === want);
      if (v)
        return {
          id: v.id,
          name: v.name,
          orb: portraitOrb(avatarThumb(v)) ?? '',
          tags: [],
          sex: card!.avatar.sex ?? null,
          c: [],
          mine: false,
          is_s2s: v.can_s2s === true,
          preview_url: v.preview_url ?? null,
        };
    }
    return null;
  }

  /* ---- готовность ---- */
  function missing(): string | null {
    const f = cur();
    if (!f) return uiText('Проверяем доступность');
    const { s, d } = live(f);
    if (s.intent && !d.orderId) return '';
    if (!s.identity && !s.integrityError) return uiText('Проверяем доступность');
    if (s.integrityError) return uiText(s.integrityError);
    if (!s.identity || s.identity.appId !== f.app.id) return uiText('Проверяем доступность');
    if (!s.form) return s.error ? '' : uiText('Проверяем доступность');
    if (s.versionStale) return '';
    if (!motionPolicy(s.form)) return uiText(s.error || 'Этот инструмент сейчас недоступен. Попробуйте обновить форму.');
    if (!s.form.available) return uiText('Создание сейчас недоступно');
    /* черновик не сохранился: кнопка «Сбросить черновик» (label, start); черновик читается — форма ещё не готова */
    if (f.draft.blocked()) return '';
    if (f.draft.restoring()) return uiText('Проверяем доступность');
    return null;
  }
  function heroInfo(): { name: string; needsPhoto?: boolean } | null {
    const w = h.who();
    if (w === 'p') {
      const p = h.dancer();
      return p ? { name: p.n, needsPhoto: !personRef(p.id).ok } : null;
    }
    return w ? { name: h.whoName() } : null;
  }
  function ready(): string {
    const f = cur();
    if (!f) return uiText('Проверяем доступность');
    const { s } = live(f);
    /* неподтверждённая загрузка: «Продолжить загрузку» (recovery) — кнопка активна; иначе — загрузка идёт */
    if (f.bridge.pending()) return f.bridge.stalled() && s.error === TRANSPORT && !s.busy ? '' : uiText('Загружаем файл');
    if (!s.form) return '';
    const w = h.who();
    if (w === 'p') {
      const ref = personRef(S.ppl[0]?.id);
      if (!ref.ok && ref.reason !== 'photos') return ref.text;
    }
    if (w === 'me' && !selfRef()) return uiText('Добавьте свои фото');
    /* права и согласие спрашивает «Создать» (лист согласия): здесь их нет */
    const miss = danceMissing(s.form, s.inputs, { hero: heroInfo(), cropOpen: !!S.crop });
    if (miss) return uiText(miss);
    /* цена «за секунду»: в заказ уходит расчёт сервера под фрагмент; до него у формы 0 — заказ получил бы 409 price_changed */
    if (s.form.chargeable && studioUnitPrice(s.form, 'second') !== null && !f.svc.derive(s).quoted) return uiText('Цена этого выбора пока не подтверждена');
    return '';
  }
  function recovery(f: Frame): ((btn: HTMLElement) => void) | null {
    const { s } = live(f);
    if ((!s.form && s.error && !s.integrityError) || s.versionStale) return () => f.svc.setters.revision(value => value + 1);
    if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return btn => void busyButton(btn, () => f.bridge.resume());
    return null;
  }
  /** Цена за секунду: только серверная цена приложения (ToolPrice per_second) у платной работы; null — неизвестна */
  function unitPrice(): number | null {
    const f = cur() ?? frame;
    const form = f?.svc.get().form;
    if (form?.chargeable === false) return null;
    return motionUnit(f?.app, form);
  }
  /** Проверка человека в ролике (dancer) сервера: только если политика её публикует; нет ответа — null */
  function humanFound(f: Frame | null): boolean | null {
    const form = f?.svc.get().form;
    if (!f || !form || motionPolicy(form)?.human_check_available !== true) return null;
    const check = f.items.video?.check;
    const item = check?.checks.find(c => c.key === 'dancer');
    return item ? item.ok : null;
  }
  const preset = (f: Frame | null = cur() ?? frame): MotionPreset => dancePreset(formOf(f), f?.app.entry as Record<string, unknown> | undefined);

  /* ---- входы ↔ S ---- */
  function commit(f: Frame | null = cur()) {
    if (!f || !f.form || !h.mounted() || h.TAIL.run) return;
    /* превью повтора ещё читается: S о файле пока не знает — входы не трогаем */
    if (f.restoring.video || f.restoring.photo) return;
    const s = f.svc.get();
    if (!s.form || f.svc.derive(s).locked) return;
    limits(f, s.form);
    const v = viewFromEngine(h, uploadsOf(f));
    f.mirror.commit(prev => inputsOf(s.form, v, prev));
    f.known = strip(f.svc.get().inputs);
  }
  function adopt(f: Frame, inputs: Inputs) {
    const form = f.svc.get().form;
    const v = viewOf(form, inputs);
    applyView(h, v);
    f.pending = { ...(v.crop ? { crop: v.crop } : {}), ...(v.trim ? { trim: v.trim } : {}) };
    if (v.who === 'ph' && S.ph && v.crop) S.ph.crop = { ...v.crop };
    if (S.clip && v.trim) S.clip.trim = { from: v.trim.start / 1000, to: v.trim.end / 1000 };
    f.known = strip(inputs);
    f.mirror.seen(inputs);
  }
  /** Пределы фрагмента по фону: сменились — фрагмент заново */
  function limits(f: Frame, form: StudioForm, force = false): boolean {
    const { minMs, maxMs } = danceLimits(form, S.bg);
    const was = h.limits();
    if (!force && was.min === minMs / 1000 && was.max === maxMs / 1000) return false;
    h.setLimits(minMs / 1000, maxMs / 1000);
    if (onForm() && cur() === f) queueMicrotask(() => onForm() && h.clipPaint());
    return true;
  }
  const swapping = () => !!h.root()?.classList.contains('leave');
  function lists(f: Frame, form: StudioForm) {
    const p = policyOf(f, 'video');
    if (p) h.UP.o.mb = p.maxBytes / MB;
    /* первая форма кадра: фрагмент заново и с подписями словаря, даже если пределы те же */
    limits(f, form, true);
  }
  /** Превью загрузки повтора: фото — с сервера; ролик сервер не отдаёт (Q7) — мост просит загрузить заново */
  function restore(f: Frame, kind: Kind, id: string) {
    if (f.restoring[kind] === id || !f.svc.get().identity) return;
    f.restoring[kind] = id;
    void f.bridge.restore(kind, id).finally(() => {
      if (f.restoring[kind] === id) delete f.restoring[kind];
      if (cur() === f && h.mounted()) formSync(f);
    });
  }
  /** Файлы в S — те, что у входов формы: прежний ушёл — убрать; id есть, файла нет — превью */
  function files(f: Frame) {
    const inputs = f.svc.get().inputs;
    for (const kind of ['video', 'photo'] as const) {
      const id = inputs[kind];
      const item = f.items[kind];
      const held = kind === 'video' ? !!S.clip : !!S.ph;
      if (item && !f.bridge.item(kind) && !id && held && item.state !== 'ready') dropFile(f, kind);
      if (id && !held && !f.restoring[kind] && !f.bridge.item(kind)) restore(f, kind, id);
    }
  }
  function dropFile(f: Frame, kind: Kind) {
    const item = f.items[kind];
    delete f.items[kind];
    if (kind === 'video') {
      if (S.clip) revoke(f, S.clip);
      S.clip = null;
      if (onForm()) h.clipPaint();
    } else {
      if (item?.url) revoke(f, { url: item.url } as EngineClip);
      S.ph = null;
      S.phOn = false;
      if (onForm()) {
        h.P.paintForm();
        h.whoSync();
      }
    }
  }
  function revoke(f: Frame, file: { url?: string; video?: string | null; poster?: string }) {
    for (const url of [file.url, file.video, file.poster])
      if (typeof url === 'string' && f.urls.delete(url))
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* адрес уже освобождён */
        }
  }
  function formSync(f: Frame) {
    const { s, d } = live(f);
    const form = s.form;
    if (form && !f.form && f.formAt !== form) {
      /* первая форма: сервис ставит форму, затем её входы (подстановка входа и повтора) отдельными шагами одного прохода — сверка после всех */
      if (!f.formWait) {
        f.formWait = true;
        queueMicrotask(() => {
          f.formWait = false;
          f.formAt = f.svc.get().form;
          changed(f);
        });
      }
      return;
    }
    if (form && f.form !== form) {
      const first = !f.form;
      f.form = form;
      /* пресет — по политике формы (вход приложения мог его не назвать) */
      const presetChanged = h.presetSet(dancePreset(form, f.app.entry as Record<string, unknown>));
      lists(f, form);
      if (first) {
        adopt(f, s.inputs);
        f.prefill = f.known;
        if (onForm() && !swapping()) h.paintPage();
      } else if (presetChanged && onForm() && !swapping()) h.paintPage();
    }
    if (!form) return;
    const it = intentOf(f);
    if (it && f.intentSeen !== it.key) {
      f.intentSeen = it.key;
      const inputs = (it.body.inputs ?? {}) as Inputs;
      if (!f.known || !sameInputs(strip(inputs), f.known)) adopt(f, inputs);
    }
    if (!d.locked && f.known && !sameInputs(strip(s.inputs), f.known)) {
      if (f.prefill && sameInputs(strip(s.inputs), f.prefill)) {
        f.known = strip(s.inputs);
        f.mirror.seen(s.inputs);
      } else {
        adopt(f, s.inputs);
        if (onForm() && !swapping()) h.paintPage();
      }
    }
    if (!onForm()) return;
    files(f);
    charFor(f);
    const error = s.integrityError ? '' : s.error;
    if (error !== f.said) {
      f.said = error;
      if (FORM_SAY.includes(error)) h.UP.say(uiText(error));
    }
    commit(f);
    h.FOOT.sync();
  }
  /** Карточка персонажа, который повторит: голос по умолчанию («Голос героя») и виды («Вид персонажа», skinSlot) */
  function charFor(f: Frame) {
    if (h.who() !== 'char') return;
    const id = num(h.curChar()?.id);
    if (!id || details.has(detailsKey(id))) return;
    void loadDetails(id).then(() => {
      if (cur() === f && onForm() && num(h.curChar()?.id) === id) {
        h.moreSync();
        h.syncFoot();
      }
    });
  }
  function changed(f: Frame) {
    if (cur() !== f || !h.mounted()) return;
    f.draft.sync();
    formSync(f);
    f.bridge.sync();
    f.driver.sync();
    f.consent?.refresh();
    balance(f);
  }
  /** Остаток для «Не хватает Circle» — тот же, что у родной кнопки (GET /app/balance, память на минуту) */
  function balance(f: Frame) {
    const orderId = f.svc.derive().orderId;
    if (orderId && orderId !== f.orderSeen) {
      f.orderSeen = orderId;
      forgetCircleBalance();
    }
    const shown = onForm(),
      fresh = shown && !f.formShown;
    f.formShown = shown;
    if (!fresh || f.balanceAsk || circleBalance() !== null || f.svc.get().form?.chargeable === false) return;
    f.balanceAsk = true;
    const seen = f.orderSeen;
    void endpoints
      .balance()
      .then(
        value => {
          if (f.orderSeen === seen) rememberCircleBalance(value?.circle);
        },
        () => {
          /* ошибка — проверки нет, решает сервер (402), как у родной кнопки */
        },
      )
      .finally(() => {
        f.balanceAsk = false;
        if (cur() === f && onForm()) h.FOOT.sync();
      });
  }

  /* ---- хвост BotRun: фасад одного экземпляра на кадр ---- */
  /** Строки шаблона (заголовки страницы заказа, подписи кнопок подвала) — функциями словаря: BotRun берёт их через val при каждом показе */
  function texts(o: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(o)) {
      if (typeof value === 'string') out[key] = () => uiText(value);
      else if (Array.isArray(value) && value.length === 3 && typeof value[2] === 'string') out[key] = [value[0], value[1], () => uiText(value[2] as string)];
      else out[key] = value;
    }
    return out;
  }
  /** Карточка заказа (runWhat шаблона) словами профиля */
  function cardText(sn: Record<string, unknown>): string {
    const ar = String(sn.arn ?? '');
    const words: Record<string, string> = { '9:16': 'Сторис', '16:9': 'Горизонтальное', '1:1': 'Квадрат', '4:5': 'Пост', '3:4': 'Портрет' };
    return [
      String(sn.name ?? ''),
      sn.clip ? uiFormat('видео {0}', String(sn.clip)) : '',
      words[ar] ? uiText(words[ar]) : ar,
      Number(sn.dur) > 0 ? mmss(Number(sn.dur)) : '',
      sn.bg === 'hero' ? uiText('фон с героя') : '',
      sn.vm !== 'src' && sn.vn ? uiFormat('голос «{0}»', String(sn.vn)) : '',
    ]
      .filter(Boolean)
      .join(' · ');
  }
  function rebuild() {
    if (!tailOpts) return;
    const { create, opts } = tailOpts;
    const base = typeof opts.missing === 'function' ? (opts.missing as () => string) : () => '';
    const T = (opts.T as Record<string, unknown>) ?? {};
    const baseDone = T.doneP;
    const hero = (opts.hero as Record<string, unknown>) ?? {};
    const baseResult = typeof hero.result === 'function' ? (hero.result as (r: BotRunRun) => string) : null;
    const foot = (opts.foot as Record<string, unknown>) ?? {};
    inst = create({
      ...opts,
      /* рамка ролика итога (BotRun.video): подпись «Смотреть» — словарём профиля */
      ...(baseResult
        ? { hero: { ...hero, result: (r: BotRunRun) => baseResult(r).replace('aria-label="Смотреть"', `aria-label="${esc(uiText('Смотреть'))}"`) } }
        : {}),
      /* карточка заказа: кто повторил, ролик, формат, длина, фон, голос — словами профиля */
      card: (r: BotRunRun) => (facade as unknown as { q(what: string): string }).q(cardText(r.snap)),
      drive: (r: BotRunRun) => server.drive(r),
      failText: (r: BotRunRun) => server.failText(r),
      receipt: (r: BotRunRun) => server.receipt(r),
      /* подвал отказов сервера и «Открыть в чате» экрана (data-dn-chat: заказ просмотрен, в Telegram — закрыть приложение) */
      foot: chatFoot(serverFoot(texts(foot), 'dn'), 'dn'),
      T: {
        ...texts(T),
        doneP: (r: BotRunRun) => {
          const text = typeof baseDone === 'function' ? String((baseDone as (r: BotRunRun) => unknown)(r)) : String(baseDone ?? '');
          return doneP((cur() ?? frame)?.svc.get().order, text, 'dn');
        },
      },
      /* «Скачать» и «Ещё вариант» итога — подписи словарём профиля в момент показа */
      acts: () => [
        ['data-dn-save', 'download', uiText('Скачать')],
        ['data-dn-again', 'refresh', uiText('Ещё вариант')],
      ],
      /* «Что дальше»: «Сделать кружок» — только когда сервер публикует маршрут (result_actions.circle_app_id); без звука — неактивна с причиной */
      next: () => nextActions(),
      steps: () => server.steps(),
      snap: () => server.snap(),
      price: () => server.sentPrice(),
      missing: () => {
        const f = cur();
        if (f) {
          const { s, d } = live(f);
          if (s.intent || d.orderId) return '';
        }
        return base();
      },
    });
    if (!Object.hasOwn(facade, 'drop')) {
      facade.start = () => {
        inst?.start();
        if (!inst?.run) return;
        startSwap = true;
        window.setTimeout(() => {
          startSwap = false;
        }, 250);
      };
      facade.drop = () => {
        if (!startSwap) inst?.drop();
      };
    }
    for (const key of Object.keys(inst)) {
      if (key === 'run' || Object.hasOwn(facade, key)) continue;
      (facade as unknown as Record<string, unknown>)[key] = (...args: unknown[]) => (inst as unknown as Record<string, (...a: unknown[]) => unknown>)[key](...args);
    }
  }
  Object.defineProperty(facade, 'run', { enumerable: true, get: () => inst?.run ?? null });
  const baseSnap = () => (tailOpts?.opts.snap as (() => Record<string, unknown>) | undefined)?.() ?? {};
  const baseSteps = () => (tailOpts?.opts.steps as (() => string[][]) | undefined)?.() ?? h.runSteps();

  /* ---- привязка кадра ---- */
  let watching = false;
  function watch() {
    if (watching) return;
    watching = true;
    watchPeople({
      paint: () => h.P.paintForm(),
      mounted: () => !!cur() && onForm(),
      changed: () => {
        commit();
        h.FOOT.sync();
      },
      m: safePeople,
    });
  }
  function bind(b: EngineBinding): EngineBound {
    watch();
    if (!frame || frame.key !== b.key || frame.svc !== b.svc) {
      if (frame) dropFrame(frame);
      frame = makeFrame(b);
    } else frame.b = b;
    const mine = frame;
    bound = mine;
    return {
      changed: () => changed(mine),
      release() {
        if (bound === mine) bound = null;
      },
      /* «назад» внутри экрана: открыт кадр фото — к форме, как «Отмена» (листы людей и «Ещё настройки» закрывает раздел сам) */
      back() {
        if (cur() !== mine || !h.mounted() || h.TAIL.run || !S.crop) return false;
        h.cropEnd(false);
        return true;
      },
    };
  }
  function dropFrame(f: Frame) {
    f.draft.stop();
    f.media.release();
    f.bridge.cancel();
    f.consent?.close();
    f.consent = null;
    f.pick.abort();
    f.doc.abort();
    f.moving?.abort();
    for (const url of f.urls)
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* адрес уже освобождён */
      }
    f.urls.clear();
  }
  function makeFrame(b: EngineBinding): Frame {
    const f = {} as Frame;
    const guard =
      <A extends unknown[]>(fn: (...args: A) => void) =>
      (...args: A) => {
        if (frame === f) fn(...args);
      };
    Object.assign(f, {
      b,
      key: b.key,
      svc: b.svc,
      app: b.app,
      mirror: createInputsMirror(b.svc),
      form: null,
      known: null,
      entry: entryOf(b),
      intentSeen: '',
      formWait: false,
      formAt: null,
      prefill: null,
      approved: new WeakSet<File>(),
      photoFiles: new WeakMap<object, File>(),
      restoredPhotos: new WeakSet<File>(),
      items: {},
      urls: new Set<string>(),
      restoring: {},
      pending: {},
      said: '',
      consent: null,
      pick: createVoicePick(),
      voices: new Map(),
      peek: null,
      formShown: false,
      balanceAsk: false,
      orderSeen: undefined,
      media: createResultMedia({ svc: b.svc, ic: (name: string) => h.ic(name) }),
      doc: screenDoc('dance', { identity: 'motion-tools', onChange: guard(() => onDoc()) }),
      moving: null,
    });
    f.bridge = createMediaBridge({
      svc: b.svc,
      owner: () => f.svc.get().identity?.owner,
      policy: key => policyOf(f, key),
      say: guard((text: string, key: string) => {
        if (key === 'photo') tip(h.root()?.querySelector('#dnPpl') ?? null, text);
        else h.UP.say(text);
      }),
      put: guard((item: MediaItem) => {
        if (!item.file) return;
        if (item.kind === 'video') {
          f.items.video = item;
          f.approved.add(item.file);
          /* ролик читает шаблон (кадр, длительность, обложка) — так же, как в демо */
          h.readClip(item.file);
        } else if (item.kind === 'photo') {
          f.items.photo = item;
          if (item.restored) {
            /* превью повтора: снимок встаёт в ряд без кадра (кадр — из входов) */
            f.restoredPhotos.add(item.file);
            f.approved.add(item.file);
            h.readPh(item.file);
          }
        }
      }),
      drop: guard((item: MediaItem | null, key: string) => {
        const kind = key === 'photo' ? 'photo' : key === 'video' ? 'video' : null;
        if (!kind || (item && f.items[kind] !== item)) return;
        dropFile(f, kind);
        commit(f);
      }),
      update: guard((item: MediaItem) => {
        const kind = item.kind === 'photo' ? 'photo' : 'video';
        if (f.items[kind] !== item) return;
        if (kind === 'video' && S.clip && item.check) {
          /* проверки сервера: звука нет, человека не нашли */
          const mute = item.check.media?.hasAudio === false;
          const nobody = humanFound(f) === false;
          if (!!S.clip.mute !== mute || !!S.clip.nobody !== nobody) {
            S.clip.mute = mute;
            S.clip.nobody = nobody;
            if (onForm()) h.clipPaint();
          }
        }
        commit(f);
        if (onForm()) {
          h.issSync();
          h.FOOT.sync();
        }
      }),
      consentWith: () => consentWith(f),
    });
    f.driver = createRunDriver({
      svc: b.svc,
      tail: () => h.TAIL,
      root: () => h.root(),
      mounted: () => cur() === f && h.mounted(),
      localSteps: () => server.steps(),
      paintForm: () => {
        h.paintPage();
      },
      tip: (el, text) => tip(el, text),
    });
    f.draft = createStudioDraft({
      svc: b.svc,
      seeded: !!b.initialInputs,
      changed: () => changed(f),
      storage: () => localStorage,
      key: motionDraftKey,
      delay: 0,
    });
    return f;
  }
  /** Лист согласия «Повтори движения»: права и consent_accepted одним касанием, правило ряда словами (MotionConsent) */
  function consentWith(f: Frame) {
    return { texts: { title: 'Права и согласие', row: 'Права на материалы', sub: RULE }, ...(consentField(f.svc, 'consent_accepted') ?? {}) };
  }
  const consentOk = (f: Frame) => {
    const s = f.svc.get();
    return s.rights && (!toolField(s.form, 'consent_accepted') || s.inputs.consent_accepted === 'true');
  };
  /** Вход экрана в контракт setup шаблона: пресет приложения, кто повторит (who, char, chars, pid), фон, голос — из входа плитки */
  function entryOf(b: EngineBinding): Record<string, unknown> {
    const entry = (b.app.entry ?? {}) as Record<string, unknown>;
    const init = (b.initialInputs ?? {}) as Record<string, unknown>;
    const o: Record<string, unknown> = { preset: dancePreset(null, entry) };
    const pick = (key: string) => init[key] ?? entry[key];
    const who = pick('who');
    if (who === 'me' || who === 'char' || who === 'ph' || who === 'none') o.who = who;
    const char = num(pick('char'));
    if (char) o.char = char;
    const raw = pick('chars');
    const chars = (Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(',') : []).map(num).filter((x): x is number => x !== null);
    if (chars.length) o.chars = chars;
    const pid = num(pick('pid'));
    if (pid) o.pid = pid;
    const bg = pick('bg');
    if (bg === 'video' || bg === 'hero') o.bg = bg;
    const v = pick('voice');
    if (v === 'src' || v === 'hero' || num(v)) o.voice = num(v) ?? v;
    return o;
  }

  /* ---- итог: «Сделать кружок» ---- */
  const circleApp = (f: Frame | null) => motionCircleAppId((f?.doc.data()?.dance as Record<string, unknown> | undefined)?.result_actions);
  function onDoc() {
    const r = h.TAIL.run;
    if (r?.st === 'done' && h.mounted()) h.TAIL.refresh();
  }
  const resultAudio = (f: Frame) => !!f.svc.get().order?.result?.assets.some(a => a.mime === 'video/mp4' && (a as { has_audio?: unknown }).has_audio === true);
  function nextActions(): string[][] {
    const f = cur() ?? frame;
    if (!f) return [];
    if (!f.doc.data()) void f.doc.load();
    if (!circleApp(f)) return [];
    const off = resultAudio(f) ? '' : ` aria-disabled="true" data-off="${esc(uiText('Кружок из этого видео пока недоступен: в результате нет подтверждённой звуковой дорожки.'))}"`;
    return [[`data-dn-circ${off}`, 'fcirc', uiText('Сделать кружок')]];
  }
  /** Индекс ролика итога: первое видео заказа, иначе первый файл; заказ не готов — null */
  function resultIndex(f: Frame): number | null {
    const order = f.svc.get().order;
    if (order?.status !== 'done' || !order.result?.available) return null;
    const assets = order.result.assets;
    return (assets.find(asset => asset.mime.startsWith('video/')) ?? assets[0])?.index ?? null;
  }
  async function play(f: Frame, el: HTMLElement, r: BotRunRun) {
    const index = resultIndex(f);
    if (index === null) return;
    const button = el.closest<HTMLElement>(RESULT_FRAMES.frame.button) ?? h.root()?.querySelector<HTMLElement>(RESULT_FRAMES.frame.button) ?? el;
    const poster = (f.svc.get().order?.result as { poster_url?: unknown } | null | undefined)?.poster_url;
    const ok = await f.media.toggle(index, { el: button, kind: 'frame', dur: Number((r.snap as { dur?: unknown }).dur) || null, poster });
    if (!ok && cur() === f) tip(button, f.svc.get().error || 'Файл результата пока недоступен. Проверьте работу в «Истории».');
  }
  function exit(f: Frame) {
    f.media.stop();
    void f.driver.leave(() => h.TAIL.exit(() => h.paintPage()), false, '');
  }
  async function toCircle(f: Frame, el: HTMLElement) {
    if (f.moving) return;
    if (el.getAttribute('aria-disabled') === 'true') {
      tip(el, el.getAttribute('data-off') ?? '');
      return;
    }
    const s = f.svc.get();
    const orderId = f.svc.derive().orderId;
    if (!orderId || s.order?.status !== 'done') return;
    const controller = new AbortController();
    f.moving = controller;
    const timer = window.setTimeout(() => controller.abort(), 30000);
    try {
      await busyButton(el, () =>
        motionToCircle({
          owner: s.intent?.owner ?? s.identity?.owner,
          orderId,
          revision: s.order?.result_revision,
          resultActions: (f.doc.data()?.dance as Record<string, unknown> | undefined)?.result_actions,
          signal: controller.signal,
        }),
      );
    } catch {
      if (f.moving === controller) tip(el, 'Не удалось передать видео. Откройте исходный заказ и попробуйте снова.');
    } finally {
      window.clearTimeout(timer);
      if (f.moving === controller) f.moving = null;
    }
  }

  /* ---- слоты «Ещё настройки» ---- */
  /** Строки слота «Ещё настройки» (название, значение, причины, строки листа) — словарём профиля; имена голосов — данные, их нет в словаре */
  function said(it: SettingSlot): SettingSlot {
    const t = (value: unknown) => (typeof value === 'string' && value ? uiText(value) : value);
    const out: SettingSlot = { ...it, t: String(t(it.t)) };
    for (const key of ['v', 'lock', 'who']) if (key in it) out[key] = t(it[key]);
    if (it.opts) out.opts = it.opts.map(o => o.map((value, i) => (i === 1 || i === 2 || i === 4 ? t(value) : value)));
    return out;
  }
  function items(L: SettingSlot[]): SettingSlot[] {
    return translated(L).map(said);
  }
  function translated(L: SettingSlot[]): SettingSlot[] {
    const form = formOf();
    if (!form) return L;
    const out: SettingSlot[] = [];
    const modes = motionVoiceModes(form);
    const audioKept = motionPolicy(form)?.original_audio_preserved === true;
    const voiceKey = motionVoiceKey(form);
    for (const it of L) {
      if (it.k === 'voice') {
        if (!toolField(form, voiceKey)) continue;
        if (it.kind === 'info') {
          out.push(it);
          continue;
        }
        if (!audioKept && modes.length < 2) {
          out.push({ k: 'voice', t: it.t, kind: 'info', v: 'Без звука', icon: h.ic('volx'), lock: 'Звук видео в результат пока не переносится' });
          continue;
        }
        const unit = motionVoiceUnit(form);
        const opts = (it.opts ?? []).filter(o => o[0] === 'src' || modes.includes(o[0] as 'hero' | 'voice'));
        const hint = unit !== null && modes.length > 1 ? `<span class="mono">+${unit}</span> ${h.esc(uiText('C в минуту'))}` : '';
        /* «Другой голос ›» — только если форма публикует выбор голоса */
        out.push(modes.includes('voice') ? { ...it, opts, hint } : { ...it, kind: 'choice', opts, hint, other: null });
        continue;
      }
      if (it.k === 'bg') {
        const bgs = danceBackgrounds(form);
        if (!bgs.length) continue;
        out.push({ ...it, opts: (it.opts ?? []).filter(o => bgs.includes(o[0] as 'video' | 'hero')) });
        continue;
      }
      out.push(it);
    }
    const skin = skinSlot();
    if (skin) out.push(skin);
    return out;
  }
  /** «Вид персонажа» (как у «Рилса с вами» и «Говорящего кружка», владелец 02.11.2026: вид — везде, где персонаж из библиотеки): виды карточки
      каталога, первым — обычный; выбор уходит в строку состава (inputsOf). Карточки ещё нет — слота нет (её грузит charFor) */
  function skinSlot(): SettingSlot | null {
    if (h.who() !== 'char') return null;
    const id = num(h.curChar()?.id);
    if (!id) return null;
    const card = details.get(detailsKey(id));
    if (card === undefined) {
      const f = cur();
      if (f) charFor(f);
    }
    if (!card) return null;
    leaseSkins(card, () => {
      if (onForm()) h.moreSync();
    });
    const base = safeMediaUrl(card.avatar.image_thumb_url || card.avatar.image_url) ?? SILHOUETTE;
    const face = (url: string | null) => `<img src="${esc(url || base)}" alt="" draggable="false">`;
    const chosen = skinOf(S.skin, id);
    return {
      k: 'skin',
      t: 'Вид персонажа',
      kind: 'faces',
      cur: chosen && card.avatar.skins.some(sk => sk.id === chosen) ? String(chosen) : 'def',
      opts: [['def', 'Обычный', '', face(base)], ...card.avatar.skins.map(sk => [String(sk.id), sk.name, '', face(skinUrl(card, sk.id))])],
      set: (v: string) => {
        const skin = Number(v);
        S.skin = Number.isSafeInteger(skin) && skin > 0 ? `${id}:${skin}` : null;
      },
    };
  }

  const server: DanceServer = {
    ui: base =>
      serverUi(base, {
        poorBalance: () => {
          const f = cur();
          if (!f || recovery(f) || f.draft.blocked()) return Infinity;
          const s = f.svc.get();
          return poorBalance({ balance: circleBalance(), form: s.form, intent: s.intent }, !!S.clip);
        },
      }),
    blank: () => SILHOUETTE,
    before: () => DANCER,
    meUrl: () => selfPicture(),
    entry(o) {
      const f = cur();
      const given = o && typeof o === 'object' ? (o as Record<string, unknown>) : {};
      return f ? { ...f.entry } : { preset: given.preset === 'swap' ? 'swap' : 'dance' };
    },
    open(o) {
      const f = cur();
      if (!f) {
        /* без привязки (кадр не активен) — форма без действий: missing() держит «Проверяем доступность» */
        h.setup(o);
        h.prep();
        h.paintPage();
        return;
      }
      const fresh = painted !== f;
      if (fresh) {
        /* другой кадр или новый сервис под прежним ключом: S, файлы, голос и хвост прежнего сюда не переходят */
        h.stop();
        h.VC.close();
        for (const [key, value] of Object.entries(JSON.parse(S0) as Record<string, unknown>)) (S as Record<string, unknown>)[key] = value;
        h.P.setIn(false);
        rebuild();
        painted = f;
        f.form = null;
        f.known = null;
        f.intentSeen = '';
      }
      f.formShown = false;
      const root = h.root();
      if (h.TAIL.run && root) {
        h.TAIL.show(root);
        changed(f);
        return;
      }
      /* вход экрана — один раз на кадр: новое открытие того же кадра правки человека не сбрасывает */
      if (fresh) {
        h.setup(o);
        h.prep();
      }
      /* документ экрана (маршрут «Сделать кружок») — заранее, как родная форма; сбой — без маршрута */
      void f.doc.load();
      f.peek = peekIntent(f.app.id);
      if (f.driver.first(f.peek) === 'form' && root) root.scrollTop = 0;
      changed(f);
    },
    close() {
      const f = frame;
      if (!f) return;
      f.consent?.close();
      f.consent = null;
      f.pick.abort();
      f.media.stop();
    },
    tail(create, opts) {
      tailOpts = { create, opts };
      rebuild();
      return facade;
    },
    wrap<T>(_slot: 'cheap', value: T): T {
      const cheap = value as unknown as { go?: unknown; where?: unknown };
      const offer = () => {
        const f = cur();
        return f ? studioCheapOffer(f.svc.get().form) : null;
      };
      /* скидку «Сделать дешевле» публикует форма (Q5): без неё у подвала нет ни цены, ни действия скидки; цена — формы, не шаблона */
      return {
        where: () => uiText(String(cheap.where ?? '')),
        get price() {
          const o = offer();
          return o ? () => o.price : undefined;
        },
        get go() {
          return offer() ? cheap.go : undefined;
        },
        get percent() {
          return offer()?.percent;
        },
      } as unknown as T;
    },
    camText: () => uiText('Камера недоступна. Загрузите файл.'),
    nobody: () => humanFound(cur()) === false,
    voice,
    heroVoice() {
      const w = h.who();
      if (w === 'char') {
        const id = charVoiceId();
        return id ? voice(id) : null;
      }
      if (w === 'me') {
        const v = h.meVar();
        return v && Array.isArray(v.voices) && v.voices.length ? voice(v.voices[0]) : null;
      }
      return null;
    },
    price(cheap) {
      const f = cur();
      const s = f?.svc.get();
      if (!f || !s?.form) return 0;
      if (cheap) {
        const offer = studioCheapOffer(s.form);
        if (offer) return offer.price;
      }
      /* расчёт сервера под нынешний ролик — цена кнопки; до него — оценка по цене секунды */
      const d = f.svc.derive(s);
      if (s.form.chargeable && d.quoted) return d.price;
      return motionPrice(f.app, s.form, s.inputs).price;
    },
    missing,
    ready,
    missText(k) {
      if (k && !CODES.has(k)) return k;
      const p = k === 'person' ? h.dancer() : null;
      return p ? uiFormat('Добавьте фото: {0}', p.n) : null;
    },
    issue() {
      const f = cur();
      const form = formOf(f);
      const c = S.clip;
      if (!f || !form || !c) return null;
      const N = (kind: string, text: string) => core().html.note(kind, text);
      const { min, max } = h.limits();
      const e = h.clipErr();
      if (e === 'short') return N('bad', uiFormat('Видео короче {0} секунд: нужно от {0} до {1} секунд', min, max));
      if (e === 'frag') return N('bad', uiFormat('Фрагмент короче {0} секунд: растяните окно', min));
      if (e === 'body') return N('bad', uiText(motionWords(preset(f)).body));
      if (e) return null;
      if (!h.whoReady()) {
        const p = h.who() === 'p' ? h.dancer() : null;
        return N('mute', p ? uiFormat('Осталось добавить фото: {0}', p.n) : uiText(motionWords(preset(f)).remain));
      }
      /* «Человек в кадре» — только по проверке сервера; без неё — длина */
      const len = `${uiText(h.fragOn() ? 'Фрагмент' : 'Длительность')} ${mmss(h.clipLen())}`;
      return N('ok', humanFound(f) === true ? `${uiText('Человек в кадре')} · ${len}` : len);
    },
    items,
    pickVoice(id) {
      const f = cur();
      const want = num(id);
      if (!f || !h.mounted() || h.TAIL.run || want === null || f.svc.derive().locked) return;
      f.pick
        .pick(want, { s2s: true })
        .then(picked => {
          if (!picked || cur() !== f || f.svc.derive().locked) return;
          if (!picked.ok) {
            tip(h.root()?.querySelector('#dnSet') ?? null, 'Этот голос не умеет переозвучку, выберите другой');
            return;
          }
          f.voices.set(picked.voice.id, engineVoice(picked.voice));
          S.vm = 'voice';
          S.voice = picked.voice.id;
          h.moreSync();
          h.syncFoot();
          h.ui().buzz();
        })
        .catch(() => tip(h.root()?.querySelector('#dnSet') ?? null, 'Не удалось выбрать голос. Попробуйте ещё раз.'));
    },
    label() {
      const f = cur();
      const act = uiText('Создать');
      if (!f) return act;
      const { s, d } = live(f);
      if (s.intent && !d.orderId) return uiText('Продолжить проверку');
      if ((!s.form && s.error && !s.integrityError) || s.versionStale) return uiText('Обновить форму');
      if (f.bridge.stalled() && !s.busy && s.error === TRANSPORT) return uiText('Продолжить загрузку');
      if (f.draft.blocked()) return uiText('Сбросить черновик');
      if (!s.form || s.form.chargeable === false) return act;
      const p = motionPrice(f.app, s.form, s.inputs);
      if (!Number.isFinite(p.price) || p.price <= 0) return act;
      const amount = cNum(server.price(S.cheap));
      /* оценка по цене за секунду: до ролика — «от» (самый короткий ролик); без цены за секунду — цена формы как есть (родная кнопка) */
      return p.estimate && !S.clip ? uiFormat('Создать · от {0} C', amount) : uiFormat('Создать · {0} C', amount);
    },
    busy() {
      const f = cur();
      const s = f?.svc.get();
      return !!s && s.busy && !s.uploadingFile;
    },
    badge() {
      const n = unitPrice();
      return n ? core().html.unit(n, uiText('за секунду')) : '';
    },
    sub() {
      const { min, max } = h.limits();
      return uiFormat(motionWords(preset()).sub, min, max);
    },
    note() {
      const f = cur() ?? frame;
      const p = f ? policyOf(f, 'video') : null;
      const kinds = p ? typeWords(p.accept) : 'MP4 или MOV';
      return `${kinds}, ${uiFormat('из длинного выберете {0} секунд', h.limits().max)}`;
    },
    upOpts: () => ({
      accept: ACCEPT,
      cap: canRecordAccepted() ? 'camera' : false,
      /* свои строки рамки BotCore.upload («Загрузить», «Снять», «Заменить», «Кадр», «Убрать», отказы) — словарём профиля */
      tx: (template: string, ...values: unknown[]) => uiFormat(template, ...values),
    }),
    trimT() {
      const max = h.limits().max;
      return { max: uiFormat('максимум {0} секунд', max), upto: `${uiText('до')} ${mmss(max)}` };
    },
    commit: () => commit(),
    admit(file, kind) {
      const f = cur();
      if (!f) return false;
      if (f.approved.has(file)) {
        f.approved.delete(file);
        return true;
      }
      if (h.TAIL.run) return false;
      const policy = policyOf(f, kind);
      if (!policy) {
        h.UP.say(uiText('Этот инструмент сейчас недоступен. Попробуйте обновить форму.'));
        return false;
      }
      if (kind === 'video') {
        void f.bridge.pick('video', file);
        return false;
      }
      /* фото: тип, вес и согласие — до кадра; загрузка — после «Готово» (родная форма: openPhoto → кадр → finishCrop) */
      const say = (text: string) => tip(h.root()?.querySelector('#dnPpl') ?? null, text);
      const typed = mediaFile.confirmType(file, 'photo');
      if (!typed || !policy.accept.includes(typed.type)) {
        say(uiFormat('Нужно фото: {0}', typeWords(policy.accept)));
        return false;
      }
      if (typed.size > policy.maxBytes) {
        say(uiFormat('Файл слишком большой. Максимум {0} МБ', Math.round(policy.maxBytes / MB)));
        return false;
      }
      if (f.svc.derive().locked) return false;
      const go = () => {
        if (cur() !== f || !onForm()) return;
        f.approved.add(typed);
        h.readPh(typed);
      };
      if (consentOk(f)) go();
      else
        f.consent = openConsentSheet({
          rights: () => f.svc.get().rights,
          locked: () => f.svc.derive().locked,
          setRights: on => f.svc.setters.rights(on),
          onContinue: () => {
            f.consent = null;
            go();
          },
          onClose: () => {
            f.consent = null;
          },
          ...consentWith(f),
        });
      return false;
    },
    accept(c) {
      const f = cur();
      if (!f) return;
      for (const url of [c.url, c.video, c.poster]) if (typeof url === 'string' && url.startsWith('blob:')) f.urls.add(url);
      const item = f.items.video;
      if (item?.check) {
        c.mute = item.check.media?.hasAudio === false;
        c.nobody = humanFound(f) === false;
      }
      if (item?.restored && f.pending.trim) c.trim = { from: f.pending.trim.start / 1000, to: f.pending.trim.end / 1000 };
    },
    remove(kind) {
      const f = cur();
      if (!f) return;
      const item = f.items[kind];
      if (kind === 'video' && S.clip) revoke(f, S.clip);
      if (item?.url) revoke(f, { url: item.url });
      delete f.items[kind];
      delete f.restoring[kind];
      f.bridge.remove(kind);
    },
    photoRead(ph, file) {
      const f = cur();
      if (!f) return false;
      if (ph.url.startsWith('blob:')) f.urls.add(ph.url);
      f.photoFiles.set(ph, file);
      if (!f.restoredPhotos.has(file)) return false;
      /* превью повтора: снимок с кадром входов — в ряд, кадр не открывается */
      f.restoredPhotos.delete(file);
      ph.crop = f.pending.crop ? { ...f.pending.crop } : null;
      S.ph = ph;
      if (f.svc.get().inputs.photo) {
        S.phOn = true;
        S.cid = null;
        S.ppl = [];
        h.P.setIn(false);
      }
      if (onForm()) {
        h.P.paintForm();
        h.whoSync();
      }
      return true;
    },
    photoChosen(ph) {
      const f = cur();
      if (!f) return;
      const file = f.photoFiles.get(ph);
      /* тот же снимок (новый кадр прежнего фото): загрузка не нужна, кадр уйдёт входами */
      if (!file || f.items.photo?.file === file) return;
      void f.bridge.pick('photo', file);
    },
    conv() {
      const it = intentOf(cur());
      const inputs = it?.body.inputs as Inputs | undefined;
      if (inputs) {
        const v = inputs.voice ?? inputs.voice_mode;
        return !!v && v !== 'src';
      }
      return h.vconv();
    },
    clipStub() {
      const it = intentOf(cur() ?? frame);
      const inputs = (it?.body.inputs ?? {}) as Inputs;
      const len = (Number(inputs.end_ms) - Number(inputs.start_ms ?? 0)) / 1000;
      const dur = Number.isFinite(len) && len > 0 ? Math.round(len) : h.limits().min;
      return { name: '', size: 0, url: '', video: null, poster: '', w: 1080, h: 1920, dur };
    },
    doneText(sn) {
      const f = cur() ?? frame;
      const form = formOf(f);
      const order = f?.svc.get().order;
      const asset = order?.result?.assets.find(a => a.mime === 'video/mp4') as { has_audio?: unknown } | undefined;
      const audio = !!form && motionPolicy(form)?.original_audio_preserved === true && asset?.has_audio !== false;
      return esc(doneText(sn, audio));
    },
    click(d, el, r) {
      const f = cur();
      const keys = ['dnRetry', 'dnRchk', 'dnFix', 'dnChat', 'dnSettings', 'dnVplay', 'dnSave', 'dnAgain', 'dnEdit', 'dnCirc', 'dnSend'];
      if (!keys.some(key => d[key] !== undefined)) return false;
      if (!f) return true;
      const order = f.svc.get().order;
      if (d.dnChat !== undefined) {
        if (r?.st === 'done')
          chatClick({ order, el, text: 'Тут откроется тема чата, куда бот прислал видео', tip: (at, text) => h.ui().tip(at, text) });
        return true;
      }
      if (d.dnSettings !== undefined) {
        openSettings();
        return true;
      }
      if (d.dnSend !== undefined) return true;
      if (d.dnVplay !== undefined) {
        if (r?.st === 'done') void play(f, el, r);
        return true;
      }
      if (d.dnSave !== undefined) {
        const index = resultIndex(f);
        if (index !== null && r?.st === 'done') {
          f.media.stop(false);
          void core().saveBtn(el, () => f.media.download(index), { buzz: () => h.ui().buzz(), tip: (at: Element, text: string) => h.ui().tip(at, text) });
        }
        return true;
      }
      if (d.dnCirc !== undefined) {
        if (r?.st === 'done') void toCircle(f, el);
        return true;
      }
      if (f.driver.busy()) return true;
      if (d.dnRetry !== undefined) h.TAIL.retry();
      else if (d.dnRchk !== undefined) void f.driver.check(el);
      /* «Ещё вариант», «Заменить видео или фото», «К настройкам»: к форме с теми же входами, без нового заказа (D10) */ else exit(f);
      return true;
    },
    start(btn) {
      const f = cur();
      if (!f) return;
      commit(f);
      const { s, d } = live(f);
      if (s.intent && !d.orderId) {
        h.TAIL.start();
        return;
      }
      const fix = recovery(f);
      if (fix) {
        fix(btn);
        return;
      }
      if (f.draft.blocked()) {
        void f.draft.reset();
        return;
      }
      /* права и согласие человека не подтверждены (повтор, перезагрузка): лист согласия, затем создание */
      if (s.form && !consentOk(f)) {
        f.consent = openConsentSheet({
          rights: () => f.svc.get().rights,
          locked: () => f.svc.derive().locked,
          setRights: on => f.svc.setters.rights(on),
          confirm: true,
          onContinue: () => {
            f.consent = null;
            if (cur() === f && onForm()) server.start(btn);
          },
          onClose: () => {
            f.consent = null;
          },
          ...consentWith(f),
          texts: { ...consentWith(f).texts, button: 'Подтвердить' },
        });
        return;
      }
      if (h.FOOT.poor()) {
        h.FOOT.poorOpen();
        return;
      }
      h.TAIL.start();
    },
    steps: () => baseSteps(),
    snap() {
      const f = cur();
      const it = intentOf(f);
      const sn = baseSnap();
      return { ...sn, cheap: !!(it ? it.body.cheap : S.cheap) };
    },
    sentPrice: () => cur()?.svc.derive().price ?? 0,
    drive(r) {
      const f = cur();
      if (f) void f.driver.drive(r);
    },
    failText(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const order = s?.order ?? null;
      const steps = tailSteps(order);
      return failText(r, {
        order,
        error: s ? s.integrityError || s.error : '',
        texts: {
          modH: 'Видео или фото не прошли проверку',
          techH: 'Не получилось собрать видео',
          notMade: uiText('видео не создано'),
          modWhy: uiText('Чужие фото без согласия и запрещённое не пропускаем.'),
        },
        step: steps.length ? steps[tailCurrent(order, steps)] : undefined,
      });
    },
    receipt(r) {
      const f = cur() ?? frame;
      const s = f?.svc.get();
      const cheap = s?.intent ? !!s.intent.body.cheap : !!r.snap.cheap;
      return receipt(r, s?.order ?? null, { cheap, where: 'на видео' });
    },
  };

  /* ---- смена языка профиля, ряд лиц, остаток ---- */
  function relabel(r: BotRunRun) {
    const fresh = server.steps();
    for (const step of r.steps) {
      const same = fresh.find(item => item[0] === step[0]);
      if (same) {
        step[2] = same[2];
        step[3] = same[3];
      }
    }
  }
  function relocale() {
    const r = h.TAIL.run;
    if (r) relabel(r);
    const f = cur();
    const root = h.root();
    if (!f || !root || !h.mounted()) return;
    if (h.TAIL.run) h.TAIL.show(root);
    else h.paintPage();
    changed(f);
  }
  document.addEventListener('steos:locale', relocale);
  document.addEventListener('steos:account-updated', () => {
    if (cur() && onForm()) h.FOOT.sync();
  });
  instance = { bind };
  return server;
}

/** BotPeople для подписи ряда лиц: у записи без фото (демо-люди шаблона до server-state) — пустой список фото */
function safePeople(): PeopleModule | null {
  const m = peopleModule();
  if (!m) return null;
  return { ...m, store: { all: () => m.store.all().map(p => ({ ...p, photos: Array.isArray(p.photos) ? p.photos : [] })) } };
}

/** ENGINES.dance.bind: привязка кадра к серверу экрана, созданному движком (без движка в серверном режиме — пустая привязка) */
export function bindDanceEngine(b: EngineBinding): EngineBound {
  return instance ? instance.bind(b) : { changed() {}, release() {} };
}

/** Вид знака заказа: dance или swap по пресету (заявка, политика формы, вход приложения), как родной хвост */
export function danceKind(s: Pick<OrderSnapshot, 'intent' | 'form'>, app: StudioApp): EngineKind {
  const sent = s.intent?.body.inputs?.preset;
  if (sent === 'swap' || sent === 'dance') return sent;
  return dancePreset(s.form, app.entry as unknown as Record<string, unknown>);
}

/** Экран «dance» реестра движков студии (swap — псевдоним canonicalScreen): движок шаблона window.BotDance, атрибуты data-dn-*,
    без правой панели; знак заказа — dance или swap по пресету */
export const DANCE_ENGINE: EngineScreen = { global: 'BotDance', prefix: 'dn', side: false, kind: (s, app) => danceKind(s, app), bind: bindDanceEngine };

export function registerDanceEngine() {
  ENGINES.dance = DANCE_ENGINE;
}
